/*
 * The app's one icon set: Hugeicons' free stroke-rounded icons (MIT). Its soft terminals suit
 * LeadCue's warm paper better than the geometric default sets. Every icon goes through here so a
 * call site names the meaning, not the glyph, and the stroke stays consistent.
 *
 * Each icon is imported from its own file, never from the package index: the index imports
 * `./Grid2x2CheckIcon.js` where the file is `Grid2X2CheckIcon.js`, which resolves on a
 * case-insensitive Mac and fails the Linux image build (deployment ajtz9ab2uyph0pojvhstayux).
 */
import AiMagicIcon from "@hugeicons/core-free-icons/AiMagicIcon";
import ArrowDown01Icon from "@hugeicons/core-free-icons/ArrowDown01Icon";
import ArrowLeft01Icon from "@hugeicons/core-free-icons/ArrowLeft01Icon";
import ArrowRight01Icon from "@hugeicons/core-free-icons/ArrowRight01Icon";
import Attachment01Icon from "@hugeicons/core-free-icons/Attachment01Icon";
import Bookmark02Icon from "@hugeicons/core-free-icons/Bookmark02Icon";
import BookOpenCheckIcon from "@hugeicons/core-free-icons/BookOpenCheckIcon";
import BotIcon from "@hugeicons/core-free-icons/BotIcon";
import Building03Icon from "@hugeicons/core-free-icons/Building03Icon";
import Calendar03Icon from "@hugeicons/core-free-icons/Calendar03Icon";
import CalendarCheck01Icon from "@hugeicons/core-free-icons/CalendarCheck01Icon";
import Cancel01Icon from "@hugeicons/core-free-icons/Cancel01Icon";
import CheckmarkCircle02Icon from "@hugeicons/core-free-icons/CheckmarkCircle02Icon";
import Clock01Icon from "@hugeicons/core-free-icons/Clock01Icon";
import Delete02Icon from "@hugeicons/core-free-icons/Delete02Icon";
import DeletePutBackIcon from "@hugeicons/core-free-icons/DeletePutBackIcon";
import Edit02Icon from "@hugeicons/core-free-icons/Edit02Icon";
import FilterHorizontalIcon from "@hugeicons/core-free-icons/FilterHorizontalIcon";
import HistoryGlyph from "@hugeicons/core-free-icons/HistoryIcon";
import HourglassIcon from "@hugeicons/core-free-icons/HourglassIcon";
import ImageOffGlyph from "@hugeicons/core-free-icons/ImageOffIcon";
import InboxIcon from "@hugeicons/core-free-icons/InboxIcon";
import KeyboardIcon from "@hugeicons/core-free-icons/KeyboardIcon";
import LeftToRightListBulletIcon from "@hugeicons/core-free-icons/LeftToRightListBulletIcon";
import Link01Icon from "@hugeicons/core-free-icons/Link01Icon";
import LinkSquare02Icon from "@hugeicons/core-free-icons/LinkSquare02Icon";
import Logout03Icon from "@hugeicons/core-free-icons/Logout03Icon";
import Mail01Icon from "@hugeicons/core-free-icons/Mail01Icon";
import MailboxGlyph from "@hugeicons/core-free-icons/MailboxIcon";
import MailOpen01Icon from "@hugeicons/core-free-icons/MailOpen01Icon";
import MailQuestionMarkIcon from "@hugeicons/core-free-icons/MailQuestionMarkIcon";
import MailRemove01Icon from "@hugeicons/core-free-icons/MailRemove01Icon";
import MailReply01Icon from "@hugeicons/core-free-icons/MailReply01Icon";
import MailsGlyph from "@hugeicons/core-free-icons/MailsIcon";
import Megaphone01Icon from "@hugeicons/core-free-icons/Megaphone01Icon";
import MegaphoneOffIcon from "@hugeicons/core-free-icons/MegaphoneOffIcon";
import Moon02Icon from "@hugeicons/core-free-icons/Moon02Icon";
import MoreHorizontalIcon from "@hugeicons/core-free-icons/MoreHorizontalIcon";
import PlusSignIcon from "@hugeicons/core-free-icons/PlusSignIcon";
import ReplyAllGlyph from "@hugeicons/core-free-icons/ReplyAllIcon";
import ReplyGlyph from "@hugeicons/core-free-icons/ReplyIcon";
import Search01Icon from "@hugeicons/core-free-icons/Search01Icon";
import SentIcon from "@hugeicons/core-free-icons/SentIcon";
import ServerIcon from "@hugeicons/core-free-icons/ServerIcon";
import Settings02Icon from "@hugeicons/core-free-icons/Settings02Icon";
import SpamIcon from "@hugeicons/core-free-icons/SpamIcon";
import Sun03Icon from "@hugeicons/core-free-icons/Sun03Icon";
import TextBoldIcon from "@hugeicons/core-free-icons/TextBoldIcon";
import TextItalicIcon from "@hugeicons/core-free-icons/TextItalicIcon";
import Tick02Icon from "@hugeicons/core-free-icons/Tick02Icon";
import UserCircleIcon from "@hugeicons/core-free-icons/UserCircleIcon";
import UserIcon from "@hugeicons/core-free-icons/UserIcon";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { LabelDot } from "./inbox-v2/label-key.tsx";
import type { NavRow } from "./shell-nav.ts";

type IconProps = { size?: number; className?: string };

function icon(glyph: IconSvgElement) {
  return function Icon({ size = 16, className }: IconProps) {
    return (
      <HugeiconsIcon
        icon={glyph}
        size={size}
        strokeWidth={1.6}
        className={className}
        aria-hidden="true"
      />
    );
  };
}

export const BoldIcon = icon(TextBoldIcon);
export const ItalicIcon = icon(TextItalicIcon);
export const ListIcon = icon(LeftToRightListBulletIcon);
export const LinkIcon = icon(Link01Icon);
export const CheckIcon = icon(Tick02Icon);
export const CloseIcon = icon(Cancel01Icon);
export const PlusIcon = icon(PlusSignIcon);
export const SendIcon = icon(SentIcon);
export const ReplyIcon = icon(MailReply01Icon);
/*
 * A message's own actions, drawn as every mail client draws them: the reply arrow, the double arrow
 * for Reply all, and the same arrow turned round for Forward (Gmail's and Outlook's forward mark).
 */
export const ReplyArrowIcon = icon(ReplyGlyph);
export const ReplyAllArrowIcon = icon(ReplyAllGlyph);
const ForwardGlyph = icon(ReplyGlyph);
export function ForwardArrowIcon({ size = 16 }: IconProps) {
  return <ForwardGlyph size={size} className="lcr-icon-mirrored" />;
}
export const ExternalLinkIcon = icon(LinkSquare02Icon);
export const LogOutIcon = icon(Logout03Icon);
export const MoonIcon = icon(Moon02Icon);
export const SunIcon = icon(Sun03Icon);
const SettingsIcon = icon(Settings02Icon);
export const ChevronDownIcon = icon(ArrowDown01Icon);
const AllStatesIcon = icon(InboxIcon);
export const BackIcon = icon(ArrowLeft01Icon);
export const ForwardIcon = icon(ArrowRight01Icon);
/**
 * The pickers a date or time field offers beside typing, and a meeting request: its rail row and the
 * marker beside an Interested label (LEA-494). Never the booked mark, which has its check.
 */
export const CalendarIcon = icon(Calendar03Icon);
export const ClockIcon = icon(Clock01Icon);
/** A label's change log, opened from the conversation's head. */
export const HistoryIcon = icon(HistoryGlyph);
/** The phone's Filters button, which folds the filter chips away. */
export const FiltersIcon = icon(FilterHorizontalIcon);
export const EditIcon = icon(Edit02Icon);
export const ShortcutsIcon = icon(KeyboardIcon);
export const ImageOffIcon = icon(ImageOffGlyph);
export const AttachmentIcon = icon(Attachment01Icon);
export const MoreIcon = icon(MoreHorizontalIcon);
/** Delete into Trash, and Restore from it (LEA-457). */
export const TrashIcon = icon(Delete02Icon);
export const RestoreIcon = icon(DeletePutBackIcon);
/** Confirm the label the AI was unsure of: a ringed check, so it is never mistaken for Done. */
export const ConfirmIcon = icon(CheckmarkCircle02Icon);
/** Mark seen or unseen, in the open conversation's More menu. */
export const SeenIcon = icon(MailOpen01Icon);

export const BookedIcon = icon(CalendarCheck01Icon);
/** One client workspace: the switcher and the settings page heading. */
export const WorkspaceIcon = icon(Building03Icon);
/** Standing answers: facts the rep has confirmed once, for the writer to reuse. */
const AnswersIcon = icon(BookOpenCheckIcon);
/** Campaigns: the outbound sequences replies come from, each with its own page. */
export const CampaignsIcon = icon(Megaphone01Icon);
export const SearchIcon = icon(Search01Icon);
/** The search typeahead's kinds: a contact who wrote, and a sender mailbox. */
export const PersonIcon = icon(UserIcon);
/** The rail's owner scope: whose threads the rows hold (Everyone, Mine, Unassigned, a colleague). */
export const ScopeIcon = icon(UserCircleIcon);
export const MailboxIcon = icon(Mail01Icon);
/** A custom (IMAP/SMTP) mailbox on the Mailboxes page. */
export const ServerMarkIcon = icon(ServerIcon);
/** The reply agent: its rail row and the head of its material in a thread. */
export const AgentIcon = icon(AiMagicIcon);

/*
 * The shell's places (`shell-nav.ts`), by name, so the rail and the phone menu draw each with the
 * same glyph. A label view is its label's dot, the same key the label pills use (`label-key.tsx`).
 * No two places share a glyph (LEA-501): All mail is a stack of mail, not Inbox's tray; Needs reply
 * the plain reply arrow, Mailboxes a mailbox, Untracked an envelope asking.
 */
const navIcons: Record<Exclude<NavRow["icon"], object>, ReturnType<typeof icon>> = {
  inbox: AllStatesIcon,
  meeting: CalendarIcon,
  "needs-reply": ReplyArrowIcon,
  "follow-up": icon(HourglassIcon),
  snoozed: ClockIcon,
  drafts: EditIcon,
  sent: SendIcon,
  done: CheckIcon,
  trash: TrashIcon,
  "auto-replies": icon(BotIcon),
  bounces: icon(MailRemove01Icon),
  "other-campaigns": icon(MegaphoneOffIcon),
  spam: icon(SpamIcon),
  untracked: icon(MailQuestionMarkIcon),
  more: MoreIcon,
  "all-mail": icon(MailsGlyph),
  view: icon(Bookmark02Icon),
  agent: AgentIcon,
  campaigns: CampaignsIcon,
  mailboxes: icon(MailboxGlyph),
  answers: AnswersIcon,
  settings: SettingsIcon,
};

export function NavGlyph({ icon: name, size }: { icon: NavRow["icon"]; size: number }) {
  if (typeof name === "object")
    return (
      <span className="lcr-nav-glyph" style={{ width: size, height: size }} aria-hidden="true">
        <LabelDot code={name.label} />
      </span>
    );
  const Icon = navIcons[name];
  return <Icon size={size} />;
}
